import * as React from "react";
import { Building2, Layers3, ServerCog, ShieldCheck, TrendingUp } from "lucide-react";
import {
  StatCard,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  BarChart,
  DonutChart,
  DonutLegend,
  PageHeader,
  PageHeaderTop,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderDescription,
  BreadcrumbItem,
  BreadcrumbPage,
  type StatCardProps,
  type DonutSegment,
} from "@eduviaos/ui-kit";
import { PlatformBreadcrumbs } from "@/components/AppBreadcrumbs";
import { useCapabilities } from "@/hooks/useCapabilities";
import {
  useSignupsByMonthMetric,
  useTenantsMetric,
  useVerticalsMetric,
  type VerticalSegment as ApiVerticalSegment,
} from "@/hooks/usePlatformMetrics";

/**
 * Super-admin overview dashboard.
 *
 * Layout:
 *   1. Hero KPI row (4 cards) — Tenants / Verticals / Capability templates
 *      / Audit events. Each StatCard carries an icon-pill + sparkline.
 *   2. Telemetry row — bar chart (signups by month, split by vertical)
 *      LEFT 2/3 + donut (vertical distribution) RIGHT 1/3.
 *   3. Capability framework status — live snapshot from
 *      /api/me/capabilities so the page proves the BE wiring works.
 *
 * Live data sources:
 *   * /api/platform/metrics/tenants        (hero KPI)
 *   * /api/platform/metrics/verticals      (donut)
 *   * /api/platform/metrics/signups-by-month?group_by_vertical=true
 *                                          (bar chart)
 *   * /api/me/capabilities                 (capability templates KPI +
 *                                           framework status section)
 */

// Sparkline trend hints — small ambient signals, not authoritative data.
// Audit-events trend stays as a placeholder until the audit-pipeline
// telemetry endpoint lands.
const SAMPLE_TREND_FLAT = [21, 22, 21, 23, 22, 21, 22, 23];
const SAMPLE_TREND_DOWN = [88, 84, 82, 81, 78, 76, 74, 72];

/** Donut palette keyed by vertical so the colour for `school` is stable
 * across the donut + the bar-chart legend. */
const VERTICAL_COLORS: Record<string, string> = {
  school: "var(--color-chart-1, oklch(0.62 0.15 155))",
  coaching_institute: "var(--color-chart-2, oklch(0.6 0.16 270))",
  study_centre: "var(--color-chart-3, oklch(0.7 0.17 50))",
  library: "var(--color-chart-4, oklch(0.65 0.13 320))",
  skill_centre: "var(--color-chart-5, oklch(0.6 0.05 250))",
  other: "var(--color-muted, oklch(0.85 0.02 240))",
};

/** Short month label (Apr / May / …) for the bar-chart x-axis from the
 * BE's YYYY-MM key. Avoids a date-fns dependency for one transformation. */
function formatMonthLabel(yyyyMm: string): string {
  const [y, m] = yyyyMm.split("-");
  if (!y || !m) return yyyyMm;
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en", {
    month: "short",
  });
}

/** Pivot the BE's flat (month, vertical, count) tuples into the
 * BarChart-friendly { month, school, coaching_institute, … } shape. */
function pivotSignups(
  buckets: { month: string; vertical: string | null; count: number }[],
): Array<Record<string, string | number>> {
  const byMonth = new Map<string, Record<string, string | number>>();
  for (const b of buckets) {
    if (!byMonth.has(b.month)) {
      byMonth.set(b.month, { month: formatMonthLabel(b.month), _key: b.month });
    }
    const row = byMonth.get(b.month)!;
    if (b.vertical) row[b.vertical] = b.count;
  }
  return Array.from(byMonth.values()).sort((a, b) => String(a._key).localeCompare(String(b._key)));
}

export function DashboardPage() {
  const caps = useCapabilities();
  const tenantsQuery = useTenantsMetric();
  const verticalsQuery = useVerticalsMetric();
  const signupsQuery = useSignupsByMonthMetric({
    months: 6,
    groupByVertical: true,
  });

  // Headline tenant count = active + suspended (matches the BE's `total`
  // which excludes archived). Falls back to `—` while the request is
  // in-flight rather than rendering zero.
  const tenantCount = tenantsQuery.data?.total;
  const verticalCount = verticalsQuery.data
    ? verticalsQuery.data.segments.filter((s) => s.count > 0).length
    : undefined;

  const stats: StatCardProps[] = [
    {
      label: "Tenants",
      value: tenantsQuery.isPending ? "—" : (tenantCount ?? 0),
      caption: tenantsQuery.data
        ? `${tenantsQuery.data.active} active · ${tenantsQuery.data.suspended} suspended`
        : "Active institutions on the platform",
      icon: <Building2 />,
      tone: "violet",
      delta: tenantsQuery.data
        ? {
            value: `${tenantsQuery.data.archived} archived`,
            trend: tenantsQuery.data.archived > 0 ? "down" : "flat",
          }
        : { value: "Loading…", trend: "flat" },
      trendData: SAMPLE_TREND_FLAT,
    },
    {
      label: "Verticals",
      value: verticalsQuery.isPending ? "—" : (verticalCount ?? 0),
      caption: verticalsQuery.data
        ? `${verticalsQuery.data.segments.length} canonical · ${verticalCount ?? 0} live`
        : "school · coaching · study centre · library · skill · other",
      icon: <Layers3 />,
      tone: "blue",
      delta: { value: "Stable", trend: "flat" },
      trendData: SAMPLE_TREND_FLAT,
    },
    {
      label: "Capability templates",
      value: caps.isPending ? "—" : caps.data ? Object.keys(caps.data.capabilities).length : 0,
      caption: "Domains under capability framework",
      icon: <ServerCog />,
      tone: "green",
      delta: { value: "All current", trend: "flat" },
      trendData: SAMPLE_TREND_FLAT,
    },
    {
      label: "Audit events (24h)",
      value: 0,
      caption: "Pending Phase 1 audit-pipeline endpoint",
      icon: <ShieldCheck />,
      tone: "amber",
      delta: { value: "Awaiting wire-up", trend: "down" },
      trendData: SAMPLE_TREND_DOWN,
    },
  ];

  /** Verticals donut segments — driven by the BE's vertical metric.
   * Stable layout: BE always returns all 6 canonical buckets even
   * when zero so the donut shape doesn't shift as tenants onboard. */
  const verticalSegments: DonutSegment[] = React.useMemo(() => {
    const apiSegments = verticalsQuery.data?.segments ?? [];
    return apiSegments.map((s: ApiVerticalSegment) => ({
      name: s.label,
      value: s.count,
      color: VERTICAL_COLORS[s.vertical] ?? "var(--color-muted, oklch(0.85 0.02 240))",
    }));
  }, [verticalsQuery.data]);

  /** BarChart series: one stack per canonical vertical. Uses the same
   * VERTICAL_COLORS map as the donut so the legend reads consistently. */
  const signupsData = React.useMemo(
    () => pivotSignups(signupsQuery.data?.months ?? []),
    [signupsQuery.data],
  );

  /** Per-vertical bar series, only for verticals that ever have a
   * non-zero bucket in the active window. Keeps the chart legend
   * uncluttered. */
  const signupsSeries = React.useMemo(() => {
    const buckets = signupsQuery.data?.months ?? [];
    const verticalsWithData = new Set<string>();
    for (const b of buckets) {
      if (b.vertical && b.count > 0) verticalsWithData.add(b.vertical);
    }
    // Always include school + coaching even if zero in window — they
    // are the canonical primary verticals. Falls back to all canonical
    // verticals if no data has come in yet.
    if (verticalsWithData.size === 0) {
      verticalsWithData.add("school");
      verticalsWithData.add("coaching_institute");
    }
    const labelByKey = new Map<string, string>();
    for (const seg of verticalsQuery.data?.segments ?? []) {
      labelByKey.set(seg.vertical, seg.label);
    }
    return Array.from(verticalsWithData).map((key) => ({
      dataKey: key,
      name: labelByKey.get(key) ?? key,
      color: VERTICAL_COLORS[key] ?? "var(--color-muted, oklch(0.85 0.02 240))",
    }));
  }, [signupsQuery.data, verticalsQuery.data]);

  return (
    <div className="grid gap-6">
      <PageHeader>
        <PageHeaderTop>
          <PlatformBreadcrumbs>
            <BreadcrumbItem>
              <BreadcrumbPage>Overview</BreadcrumbPage>
            </BreadcrumbItem>
          </PlatformBreadcrumbs>
        </PageHeaderTop>
        <PageHeaderHeading>
          <PageHeaderTitle>Platform overview</PageHeaderTitle>
          <PageHeaderDescription>
            Cross-tenant operational health: signups, vertical mix, capability framework state.
          </PageHeaderDescription>
        </PageHeaderHeading>
      </PageHeader>

      <section
        aria-label="At-a-glance platform metrics"
        className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
      >
        {stats.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
      </section>

      <section aria-label="Platform telemetry" className="grid gap-3 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-start justify-between space-y-0">
            <div className="grid gap-1">
              <CardTitle className="text-base">Tenant signups · 6 months</CardTitle>
              <CardDescription>
                New tenants onboarded each month, split by vertical.
              </CardDescription>
            </div>
            <div className="border-border bg-muted/40 flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs">
              <TrendingUp
                className="size-3.5 text-emerald-600 dark:text-emerald-400"
                aria-hidden="true"
              />
              <span className="text-foreground font-medium">Live</span>
              <span className="text-muted-foreground">IST month</span>
            </div>
          </CardHeader>
          <CardContent>
            {signupsQuery.isError ? (
              <p className="py-12 text-center text-sm text-rose-600 dark:text-rose-400">
                {signupsQuery.error instanceof Error
                  ? signupsQuery.error.message
                  : "Failed to load signups time-series."}
              </p>
            ) : signupsQuery.isPending ? (
              <div className="bg-muted/40 h-[240px] animate-pulse rounded" />
            ) : (
              <BarChart data={signupsData} xKey="month" height={240} series={signupsSeries} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Verticals</CardTitle>
            <CardDescription>Distribution of active tenants.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            {verticalsQuery.isError ? (
              <p className="py-12 text-center text-sm text-rose-600 dark:text-rose-400">
                {verticalsQuery.error instanceof Error
                  ? verticalsQuery.error.message
                  : "Failed to load verticals."}
              </p>
            ) : verticalsQuery.isPending ? (
              <div className="bg-muted/40 h-[200px] animate-pulse rounded" />
            ) : (verticalsQuery.data?.total ?? 0) > 0 ? (
              <>
                <DonutChart
                  segments={verticalSegments}
                  centerValue={verticalsQuery.data?.total ?? 0}
                  centerLabel="tenants"
                  height={200}
                />
                <DonutLegend segments={verticalSegments} />
              </>
            ) : (
              <p className="text-muted-foreground py-12 text-center text-sm">
                No tenants onboarded yet.
              </p>
            )}
          </CardContent>
        </Card>
      </section>

      <section aria-label="Capability framework status" className="grid gap-3">
        <div>
          <h2 className="text-base font-semibold tracking-tight">Capability framework</h2>
          <p className="text-muted-foreground text-sm">
            Live snapshot from{" "}
            <code className="bg-muted rounded px-1 py-0.5 text-[11px]">
              GET /api/me/capabilities
            </code>{" "}
            — same source-of-truth that drives every tenant portal.
          </p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {caps.isPending
                ? "Loading…"
                : caps.isError
                  ? "Capabilities unavailable"
                  : `${Object.keys(caps.data?.capabilities ?? {}).length} active`}
            </CardTitle>
            <CardDescription>
              {caps.data
                ? `Vertical: ${caps.data.vertical} · Cache ${caps.data.cache}`
                : "Awaiting response from backend"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {caps.isError ? (
              <p className="text-sm text-rose-600 dark:text-rose-400">
                {caps.error instanceof Error ? caps.error.message : "Unknown error"}
              </p>
            ) : caps.data ? (
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(caps.data.capabilities).map(([key, value]) => (
                  <Badge
                    key={key}
                    variant={
                      value.source === "branch"
                        ? "warn"
                        : value.source === "tenant"
                          ? "info"
                          : "secondary"
                    }
                    className="font-mono text-[10px]"
                  >
                    {key} · v{value.schema_version}
                  </Badge>
                ))}
              </div>
            ) : (
              <div className="bg-muted/50 h-12 animate-pulse rounded" />
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
